<template>
	<UserAssignSelectContainerSkeleton
		v-if="showAssigneeSkeleton && enableLoading"
		:users-count="userIds.length"
		:size="size"
		:max-avatars-shown="maxAvatarsShownWithoutHiding"
		:is-owner-enabled="isOwnerEnabled"
	/>

	<div v-else class="user-assign-select" :class="classAddons">
		<UserAssignSelectOwnerDropdown
			v-if="isOwnerEnabled"
			:is-open="isOwnerSelectOpen"
			:is-dropdown-enabled="isDropdownEnabled && isOwnerChangeEnabled"
			:is-modify-enabled="isModifyEnabled"
			:owner="owner"
			:dynamic-owner="dynamicOwner"
			:entity-owner="entityOwner"
			:potential-owners="potentialOwners"
			:owner-title="ownerTitle"
			:search-query="ownerSearchQuery"
			:avatar-size="avatarSize"
			:loading-users-and-groups="loadingUsersAndGroups"
			:size="size"
			@update:is-open="isOwnerSelectOpen = $event"
			@update:search-query="ownerSearchQuery = $event"
			@toggle-open="toggleIsOwnerSelectOpen"
			@avatar-click="onOwnerAvatarClicked"
			@avatar-dblclick="onOwnerAvatarDblClicked"
			@owner-click="onOwnerClicked"
			@assign-owner="onAssignOwner"
		/>

		<SuiIcon
			v-if="isOwnerEnabled"
			name="icon-arrow-right-16"
			class="user-assign-select__arrow-icon"
		/>

		<div ref="$avatarWrapper" class="user-assign-select__dropdown-wrapper">
			<VDropdown
				class="user-assign-select__dropdown"
				:shown="isUserSelectOpen"
				placement="right-start"
				distance="8"
				:triggers="[]"
				:hide-triggers="['click']"
				:skidding="-8"
				:disabled="!isDropdownEnabled"
				:flip="false"
				@apply-hide="isUserSelectOpen = false"
				@apply-show="onDropdownShow"
			>
				<UserAssignSelectTrigger
					:has-no-members="hasNoMembers"
					:no-members-tooltip="noMembersTooltip"
					:is-dropdown-enabled="isDropdownEnabled"
					:assigned-users-with-avatars="assignedUsersWithAvatars"
					:assigned-roles="assignedRolesWithAvatars"
					:is-extra-count-shown="isExtraCountShown"
					:hidden-count="hiddenCount"
					:avatar-size="avatarSize"
					:size="size"
					:confirmed-allocated-times="confirmedAllocatedTimes"
					:hidden-users="hiddenUsers"
					:hidden-roles="hiddenRoles"
					:is-user-select-open="isUserSelectOpen"
					:is-tooltip-enabled="isTooltipEnabled"
					@click="onMemberAvatarClicked"
					@dblclick="onMemberAvatarDblClick"
				/>
				<template #popper>
					<SuiSelectList ref="$selectList" @navigate="scrollToIndex">
						<template #header>
							<UserAssignSelectHeader
								:title="title"
								:search-query="searchQuery"
								:enable-role-selection="enableRoleSelection"
								:has-individual-time-allocation="
									hasIndividualTimeAllocation
								"
								:users-list="usersList"
								:selected-users="selectedUsers"
								:selected-roles="selectedRoles"
								:pinned-user-positions="pinnedUserPositions"
								:has-custom-allocation="hasCustomAllocation"
								:confirmed-allocated-times="
									confirmedAllocatedTimes
								"
								:allocated-times="allocatedTimes"
								:task-users-availability="taskUsersAvailability"
								:active-role-filter-id="activeRoleFilterId"
								@close="onHeaderClose"
								@search="searchQuery = $event"
								@unselect="onUserClicked($event)"
								@update:has-custom-allocation="
									onUpdateHasCustomAllocation($event)
								"
								@update:allocated-times="
									$emit('update:allocated-times', $event)
								"
								@update:active-role-filter-id="
									onRoleFilterClicked
								"
							/>
						</template>
						<UserAssignSelectList
							:filtered-user-list="filteredUserList"
							:is-modify-enabled="isModifyEnabled"
							:is-all-users-selected="isAllUsersSelected"
							:selected-users="selectedUsers"
							:selected-roles="selectedRoles"
							:user-group-list="userGroupList"
							:selected-user-group="selectedUserGroup"
							:loading-users-and-groups="loadingUsersAndGroups"
							:closed-groups="closedGroups"
							:header-element="headerElement"
							:task-users-availability="taskUsersAvailability"
							:active-role-filter-id="activeRoleFilterId"
							:grouped-virtual-list="groupedVirtualList"
							:virtual-list-attrs="virtualListAttrs"
							:measure-element="measureElement"
							@select-all="onSelectAll"
							@select-user-group="onSelectUserGroup"
							@deselect-user-group="onDeselectUserGroup"
							@toggle-group-dropdown="onToggleGroupDropdown"
							@toggle-all-users-selected="toggleAllUsersSelected"
							@user-clicked="onUserClicked"
							@role-clicked="onRoleClicked"
						/>
						<template v-if="isModifyEnabled" #footer>
							<UserAssignSelectFooter
								:task-id="taskId"
								:should-notify="shouldNotify"
								:active-role-filter-id="activeRoleFilterId"
								@update:should-notify="shouldNotify = $event"
								@assign-users="onAssignUsers"
								@remove-role="onRemoveRole"
							/>
						</template>
					</SuiSelectList>
				</template>
			</VDropdown>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { ref, computed, toRefs, onMounted, watch, nextTick } from 'vue';
import {
	SuiSelectList,
	SuiIcon,
	type SuiSelectListGroupType,
	type SuiSelectListItemsAndGroupsValueType,
} from '@developers/scoro-ui';
import {
	type User,
	UserType,
	UserStatus,
	type Project,
	useUserAssignSelectUsersLazyQuery,
	UserAssignSelectUsersDocument,
	useUserAssignSelectUsersAndGroupsLazyQuery,
	UserAssignSelectUsersAndGroupsDocument,
	type UserAssignSelectUsersQuery,
	type Task,
	type UserAssignSelectUserFragment,
	type Role,
	useUserAssignSelectAvailabilityLazyQuery,
	UserAssignSelectAvailabilityDocument,
	type UserAssignSelectAvailabilityQuery,
	type Contact,
	type DynamicOwnerEnum,
} from '@/generated/graphql';
import uniq from 'lodash/uniq';
import { useToggle } from '@vueuse/core';
import {
	UserAssignGroups,
	type PinnedUserPositions,
	type UserAssignAvatarSize,
	type UserAssignGroup,
	type UserAssignSelectRole,
	type UserAssignSelectAssigneesPayload,
} from '../../types';
import {
	FILTERED_USER_TYPES,
	MAX_AVATARS_SHOWN_WITHOUT_HIDING,
	UserAssignSelectSize,
} from '../../constants';
import UserAssignSelectContainerSkeleton from './UserAssignSelectContainerSkeleton.vue';
import { type ApolloQueryResult } from '@apollo/client/core';
import { useTranslation } from '@/composables';
import { type ValueOf } from '@/types';
import {
	type TaskAssigneeInputDuration,
	type TaskAssigneeAllocatedDuration,
} from '@/features/Task/components/TaskUsers/types';
import { useCurrentUser } from '@/features/Auth';
import { UserAssignSelectHeader } from './Header';
import { UserAssignSelectList } from './List';
import UserAssignSelectOwnerDropdown from './UserAssignSelectOwnerDropdown.vue';
import UserAssignSelectTrigger from './UserAssignSelectTrigger.vue';
import UserAssignSelectFooter from './UserAssignSelectFooter.vue';
import { getInitials, sortUsers } from '../../utils';
import { secondsToTimeAllocation } from '@/utils/time';
import {
	useUserAssignRoleFilter,
	useUserAssignVirtualList,
} from '../../composables';

export type UserAssignProps = {
	title?: string;
	ownerTitle?: string;
	subGroupTitles?: string[];
	owner: UserAssignSelectUserFragment | undefined | null;
	dynamicOwner?: DynamicOwnerEnum | null;
	userIds: Array<User['id']>;
	assignedRoles?: Array<UserAssignSelectRole>;
	isModifyEnabled: boolean;
	isDropdownEnabled: boolean;
	projectId?: Project['id'];
	isOwnerExcludedFromSelection?: boolean;
	loadProjectMembers?: boolean;
	maxAvatarsShownWithoutHiding?: number;
	isOwnerEnabled?: boolean;
	isOwnerChangeEnabled?: boolean;

	isGroupedByRoles?: boolean;
	taskId?: Task['id'];
	noMembersText?: string;
	size?: ValueOf<typeof UserAssignSelectSize>;
	hasIndividualTimeAllocation?: boolean;
	allocatedTimes?: TaskAssigneeAllocatedDuration;
	confirmedAllocatedTimes?: TaskAssigneeAllocatedDuration;
	hasCustomAllocation?: Task['hasCustomAllocation'];
	filterByRoleId?: Role['id'];
	enableLoading?: boolean;
	plannedDuration?: Task['durationPlanned'];
	preloadedUsers?: UserAssignSelectUserFragment[];
	/**
	 * Contact ID to filter CUPO users by.
	 * Only shows CUPO users related to the specified contact
	 */
	contactId?: Contact['id'];
	/**
	 * Enable double click shortcuts for selecting users
	 * - Double click on owner avatar selects owner
	 * - Double click on member avatar selects current user if it is not already in selection
	 * - Double click on member avatar unasigns all users if current user is already in selection
	 */
	enableShortcuts?: boolean;
	/**
	 * Enable role selection in the dropdown list.
	 * Defaults to false to ensure roles are only shown where explicitly needed (e.g., task pages).
	 */
	enableRoleSelection?: boolean;
	/**
	 * Enable tooltip on extra count avatar showing hidden users/roles.
	 * Defaults to false. Set to true for task detailed view.
	 */
	isTooltipEnabled?: boolean;
};

const props = withDefaults(defineProps<UserAssignProps>(), {
	title: undefined,
	ownerTitle: undefined,
	subGroupTitles: () => [],
	projectId: undefined,
	isOwnerExcludedFromSelection: true,
	loadProjectMembers: false,
	maxAvatarsShownWithoutHiding: MAX_AVATARS_SHOWN_WITHOUT_HIDING,
	isOwnerEnabled: true,
	isOwnerChangeEnabled: true,
	dynamicOwner: null,
	taskId: undefined,
	noMembersText: undefined,
	size: UserAssignSelectSize.MEDIUM,
	allocatedTimes: undefined,
	confirmedAllocatedTimes: undefined,
	hasCustomAllocation: false,
	filterByRoleId: undefined,
	enableLoading: true,
	preloadedUsers: () => [],
	plannedDuration: undefined,
	contactId: undefined,
	assignedRoles: () => [],
	enableRoleSelection: false,
	isTooltipEnabled: false,
});

const emit = defineEmits<{
	(e: 'update:assignees', payload: UserAssignSelectAssigneesPayload): void;
	(
		e: 'update:owner',
		owner: UserAssignSelectUserFragment | undefined | null
	): void;
	(e: 'update:allocated-times', payload: TaskAssigneeInputDuration): void;
	(e: 'update:has-custom-allocation', value: boolean): void;
	(e: 'role-replaced-with-user'): void;
}>();

const { isModifyEnabled, projectId, contactId } = toRefs(props);

const closedGroups = ref<UserAssignGroup['id'][]>([]);
const closedGroupsBeforeFilter = ref<UserAssignGroup['id'][]>([]);
const activeRoleFilterId = ref<UserAssignSelectRole['id'] | null>(null);

const { currentUser } = useCurrentUser();
const [isOwnerSelectOpen, toggleIsOwnerSelectOpen] = useToggle();
const [isUserSelectOpen, toggleIsUserSelectOpen] = useToggle();
const { t } = useTranslation();
const shouldNotify = ref(false);
const $avatarWrapper = ref<HTMLElement>();
const $selectList = ref<InstanceType<typeof SuiSelectList> | null>(null);
const containerWidth = ref(0);
const isInitialMembersLoading = ref(true);
const avatarWidthLarge = 40;
const avatarGapLarge = 16;

const headerElement = () => document.querySelector('.sui-select-list__header');
const listContentElement = () =>
	document.querySelector('.sui-select-list__content');

const baseUsersQueryFilters = computed(() => ({
	userTypes: FILTERED_USER_TYPES,
	projectId: projectId.value,
	contactId: contactId.value,
}));

const userAssigneeQueryFilters = computed(() => ({
	...baseUsersQueryFilters.value,
	ids: [
		...props.userIds.map((id) => Number(id)),
		...(props.owner?.id ? [Number(props.owner.id)] : []),
	],
}));

const baseProjectQueryParams = {
	projectParams: {
		projectId: projectId.value ? Number(projectId.value) : undefined,
	},
	loadProject: Boolean(props.loadProjectMembers && projectId.value),
};

const {
	load: loadMembers,
	loading: loadingMembers,
	onResult: onResultMembers,
	refetch: refetchMembers,
} = useUserAssignSelectUsersLazyQuery({
	input: {
		filters: userAssigneeQueryFilters.value,
	},
	...baseProjectQueryParams,
});

const showAssigneeSkeleton = computed(
	() => isInitialMembersLoading.value && loadingMembers.value
);

const {
	load,
	result,
	loading: loadingUsersAndGroupsQuery,
	onResult: onResultAllUsers,
} = useUserAssignSelectUsersAndGroupsLazyQuery({
	usersListInput: {
		filters: {
			...baseUsersQueryFilters.value,
		},
	},
	rolesListInput: {
		filters: {
			isActive: true,
		},
	},
	loadRoles: props.enableRoleSelection,
	...baseProjectQueryParams,
});

const hasLoadedAllUsers = ref(false);
const loadingUsersAndGroups = computed(
	() => loadingUsersAndGroupsQuery.value || !hasLoadedAllUsers.value
);

const onDropdownShow = () => {
	// Delay needed - SuiSelectListSearch autofocus fires before popover is fully rendered. nextTick would still be too early.
	setTimeout(() => {
		const input = $selectList.value?.$el?.querySelector(
			'.sui-select-list-search input'
		) as HTMLInputElement | null;
		input?.focus();
	}, 50);
};

const {
	load: loadTaskUsersAvailability,
	refetch: refetchTaskUsersAvailability,
	result: taskUsersAvailabilityResult,
} = useUserAssignSelectAvailabilityLazyQuery();

const loadUsersAndGroups = () => {
	load(UserAssignSelectUsersAndGroupsDocument, {
		usersListInput: {
			filters: { ...baseUsersQueryFilters.value, taskId: props.taskId },
		},
		rolesListInput: {
			filters: {
				isActive: true,
			},
		},
		loadRoles: props.enableRoleSelection,
		...baseProjectQueryParams,
	});
};

const loadUsersAvailability = (userIds?: Array<User['id']>) => {
	if (!props.taskId) {
		return;
	}

	loadTaskUsersAvailability(UserAssignSelectAvailabilityDocument, {
		input: {
			taskId: props.taskId,
			userIds: userIds?.map((id) => String(id)),
		},
	});
};

const searchQuery = ref('');
const ownerSearchQuery = ref('');

const taskUsersAvailability = computed<
	UserAssignSelectAvailabilityQuery['TaskUsersAvailability']
>(() => taskUsersAvailabilityResult.value?.TaskUsersAvailability ?? []);

const selectedUsers = ref(props.userIds);
const selectedRoles = ref<UserAssignSelectRole[]>(props.assignedRoles);
const entityOwner = ref(props.owner);
const usersList = ref<UserAssignSelectUserFragment[]>([]);
const projectMembers = ref<Array<User['id']>>([]);

const pinnedUserPositions = ref<PinnedUserPositions>(new Map());

const potentialOwners = computed(() =>
	usersList.value
		.filter(
			(user) =>
				user.type !== UserType.Cupo &&
				(user.status !== UserStatus.Inactive ||
					user.id === props.owner?.id) &&
				user.name
					.toLowerCase()
					.trim()
					.includes(ownerSearchQuery.value.toLowerCase().trim())
		)
		.sort((a, b) =>
			a.id === props.owner?.id
				? -1
				: b.id === props.owner?.id
					? 1
					: a.name.localeCompare(b.name)
		)
);

const hasNoMembers = computed(() => {
	if (props.filterByRoleId) {
		const usersWithRole = usersList.value.filter(
			(user) => user.role?.id === props.filterByRoleId
		);

		return (
			usersWithRole.length === 0 ||
			usersWithRole.every((user) => !props.userIds.includes(user.id))
		);
	}

	return props.userIds.length === 0 && props.assignedRoles.length === 0;
});
const noMembersTooltip = computed(
	() => props.noMembersText ?? t('user_assign_select_add_members')
);

const normalizedSearchQuery = computed(() =>
	searchQuery.value.toLowerCase().trim()
);

const isSearchMatch = (text: string | undefined | null): boolean => {
	if (!text) {
		return false;
	}

	return text.toLowerCase().trim().includes(normalizedSearchQuery.value);
};

const filteredUserList = computed(() => {
	if (!usersList.value) {
		return [];
	}

	let userListFiltered = usersList.value;

	if (props.isOwnerExcludedFromSelection) {
		userListFiltered = usersList.value.filter(
			(user) => user.id !== props.owner?.id
		);
	}

	return userListFiltered.filter((user) => {
		const nameMatches = isSearchMatch(user.name);

		if (props.enableRoleSelection) {
			const roleNameMatches = isSearchMatch(user.role?.name);
			return nameMatches || roleNameMatches;
		}

		return nameMatches;
	});
});

const projectMembersList = computed(() => {
	if (!filteredUserList.value) {
		return [];
	}

	return filteredUserList.value.filter((user) =>
		// In this group potential we could have INACTIVE users as well
		projectMembers.value.includes(user.id)
	);
});

const filterUnassignedUsers = (isCupoUser: boolean) => {
	if (!filteredUserList.value) {
		return [];
	}

	return filteredUserList.value.filter(
		(user) =>
			!projectMembers.value.includes(user.id) &&
			user.status !== UserStatus.Inactive &&
			(isCupoUser
				? user.type === UserType.Cupo
				: user.type !== UserType.Cupo)
	);
};

const unAssignedUserList = computed(() => filterUnassignedUsers(false));
const unAssignedCupoUserList = computed(() => filterUnassignedUsers(true));

const dynamicMaxAvatars = computed(() => {
	if (
		props.size !== UserAssignSelectSize.LARGE ||
		containerWidth.value === 0
	) {
		return props.maxAvatarsShownWithoutHiding;
	}

	const avatarWithGap = avatarWidthLarge + avatarGapLarge;
	const maxPossibleAvatars = Math.floor(containerWidth.value / avatarWithGap);

	return Math.max(
		1,
		Math.min(maxPossibleAvatars, props.maxAvatarsShownWithoutHiding)
	);
});

const isExtraCountShown = computed(() => {
	const countOfUsers =
		usersList.value.filter(
			(user) =>
				props.userIds.includes(user.id) && user.type !== UserType.Cupo
		) ?? [];

	const totalItems = props.assignedRoles.length + countOfUsers.length;
	return totalItems > dynamicMaxAvatars.value;
});

const assignedRolesWithAvatars = computed(() => {
	if (!isExtraCountShown.value) {
		return props.assignedRoles;
	}

	return props.assignedRoles.slice(
		0,
		Math.min(props.assignedRoles.length, dynamicMaxAvatars.value - 1)
	);
});

const availableUserSlots = computed(() => {
	return Math.max(
		0,
		dynamicMaxAvatars.value - 1 - assignedRolesWithAvatars.value.length
	);
});

const assignedUsersWithAvatars = computed(() => {
	let allAssignedUsers =
		usersList.value.filter(
			(item) =>
				props.userIds.includes(item.id) && item.type !== UserType.Cupo
		) ?? [];

	if (props.filterByRoleId) {
		allAssignedUsers = allAssignedUsers.filter(
			(user) => user.role?.id === props.filterByRoleId
		);
	}

	const allAssignedCupoUsers =
		usersList.value.filter(
			(item) =>
				props.userIds.includes(item.id) && item.type === UserType.Cupo
		) ?? [];

	if (isExtraCountShown.value) {
		return [
			...allAssignedUsers.slice(0, availableUserSlots.value),
			...allAssignedCupoUsers,
		];
	} else {
		return [...allAssignedUsers, ...allAssignedCupoUsers];
	}
});

const hiddenCount = computed(() => {
	const assignedCupoUsers =
		usersList.value.filter(
			(user) =>
				props.userIds.includes(user.id) && user.type === UserType.Cupo
		) ?? [];

	const allNonCupoUsers = props.userIds.length - assignedCupoUsers.length;
	const visibleRoleCount = assignedRolesWithAvatars.value.length;

	const hiddenUsers = allNonCupoUsers - availableUserSlots.value;
	const hiddenRoles = props.assignedRoles.length - visibleRoleCount;

	return hiddenUsers + hiddenRoles;
});

const hiddenUsers = computed((): UserAssignSelectUserFragment[] => {
	const visibleUserIds = assignedUsersWithAvatars.value.map(
		(u: UserAssignSelectUserFragment) => u.id
	);
	return usersList.value.filter(
		(user) =>
			props.userIds.includes(user.id) &&
			user.type !== UserType.Cupo &&
			!visibleUserIds.includes(user.id)
	);
});

const hiddenRoles = computed((): UserAssignSelectRole[] => {
	const visibleRoleIds = assignedRolesWithAvatars.value.map((r) => r.id);
	return props.assignedRoles.filter(
		(role) => !visibleRoleIds.includes(role.id)
	);
});

const isAllProjectMembersSelected = computed(() => {
	if (projectMembersList.value?.length === 0) {
		return false;
	}
	const notSelectedUsers = projectMembersList.value.filter(
		(member) => !selectedUsers.value.includes(member.id)
	);

	return notSelectedUsers.length > 0 ? false : true;
});

const isAllNotAssignedUsersSelected = computed(() => {
	if (unAssignedUserList.value?.length === 0) {
		return false;
	}
	const notSelectedUsers = unAssignedUserList.value.filter(
		(user) => !selectedUsers.value.includes(user.id)
	);

	return notSelectedUsers.length > 0 ? false : true;
});

const isAllNotAssigneCupoUsersSelected = computed(() => {
	if (unAssignedCupoUserList.value?.length === 0) {
		return false;
	}
	const notSelectedCupoUsers = unAssignedCupoUserList.value.filter(
		(user) => !selectedUsers.value.includes(user.id)
	);

	return notSelectedCupoUsers.length > 0 ? false : true;
});

const isAllUsersSelected = computed(() => {
	if (filteredUserList.value?.length === 0) {
		return false;
	}
	const notSelectedUsers = filteredUserList.value.filter(
		(user) => !selectedUsers.value.includes(user.id)
	);

	return notSelectedUsers.length > 0 ? false : true;
});

const buildRoleGroupsForAssignRolesToTasks = (
	groups: UserAssignGroup[]
): UserAssignGroup[] => {
	const rolesList = result.value?.RolesList ?? [];

	const roleGroups = [...rolesList]
		.sort((a, b) => a.name.localeCompare(b.name))
		.map((role) => {
			let usersInRole = unAssignedUserList.value.filter(
				(user) => user.role?.id === role.id
			);

			if (activeRoleFilterId.value) {
				usersInRole = usersInRole.filter(
					(user) => !selectedUsers.value.includes(user.id)
				);
			}

			const isRoleMatchesSearch = isSearchMatch(role.name);

			const shouldShowRoleItem =
				!activeRoleFilterId.value &&
				(isRoleMatchesSearch || usersInRole.length > 0);

			const roleWithInitials = {
				...role,
				initials: role.initials || getInitials(role.name),
			};

			return {
				id: role.name,
				name: role.name,
				role: roleWithInitials,
				isRoleSelectable:
					props.enableRoleSelection &&
					shouldShowRoleItem &&
					isRoleMatchesSearch,
				userList: usersInRole,
				isAllUsersSelected: usersInRole.every((user) =>
					selectedUsers.value.includes(user.id)
				),
			};
		});

	if (activeRoleFilterId.value || normalizedSearchQuery.value !== '') {
		groups[0] = {
			...groups[0],
			userList: groups[0].userList.filter((user) => {
				const isVisibleByName =
					normalizedSearchQuery.value === '' ||
					isSearchMatch(user.name);
				const isAvailableForSelection =
					!activeRoleFilterId.value ||
					!selectedUsers.value.includes(user.id);

				return isVisibleByName && isAvailableForSelection;
			}),
		};
	}

	return [...groups, ...roleGroups];
};

const userGroupListToDisplay = computed(() => {
	let groups: UserAssignGroup[] = [
		{
			id: UserAssignGroups.ProjectMembers,
			name: props.subGroupTitles[0],
			userList: projectMembersList.value,
			isAllUsersSelected: isAllProjectMembersSelected.value,
			role: null,
			isRoleSelectable: false,
		},
	];

	if (props.enableRoleSelection) {
		groups = buildRoleGroupsForAssignRolesToTasks(groups);
	} else {
		if (props.isGroupedByRoles) {
			const groupedByRoles: Record<
				Role['name'],
				UserAssignSelectUserFragment[]
			> = unAssignedUserList.value.reduce(
				(acc, user) => {
					const roleId = user.role?.name || props.subGroupTitles[1];
					if (!acc[roleId]) {
						acc[roleId] = [];
					}
					acc[roleId].push(user);
					return acc;
				},
				{} as Record<Role['name'], UserAssignSelectUserFragment[]>
			);

			groups = [
				...groups,
				...Object.entries(groupedByRoles).map(([roleName, users]) => ({
					id: roleName,
					name: roleName,
					role: null,
					isRoleSelectable: false,
					userList: users,
					isAllUsersSelected: users.every((user) =>
						selectedUsers.value.includes(user.id)
					),
				})),
			];
		} else if (isModifyEnabled.value) {
			groups = [
				...groups,
				{
					id: UserAssignGroups.Other,
					name: props.subGroupTitles[1],
					userList: unAssignedUserList.value,
					isAllUsersSelected: isAllNotAssignedUsersSelected.value,
					role: null,
					isRoleSelectable: false,
				},
				{
					id: UserAssignGroups.Cupo,
					name: props.subGroupTitles[2],
					userList: unAssignedCupoUserList.value,
					isAllUsersSelected: isAllNotAssigneCupoUsersSelected.value,
					role: null,
					isRoleSelectable: false,
				},
			];
		}
	}

	return groups.filter(
		(group) => group.isRoleSelectable || group.userList.length > 0
	);
});

const { scrollToGroup } = useUserAssignRoleFilter({
	userGroupListToDisplay,
	activeRoleFilterId,
});

const scrollElement = computed(
	() => $selectList.value?.$refs?.selectListContent as HTMLElement | null
);

const { groupedVirtualList, virtualListAttrs, measureElement, scrollToIndex } =
	useUserAssignVirtualList({
		userGroupListToDisplay,
		closedGroups,
		scrollElement,
	});

const onOwnerAvatarClicked = () => {
	if (isModifyEnabled.value) {
		loadUsersAndGroups();

		isOwnerSelectOpen.value = true;
		ownerSearchQuery.value = '';
		entityOwner.value = props.owner;
	}
};

const updateGroupsForRoleFilter = () => {
	if (!activeRoleFilterId.value) {
		// Role cleared - restore previous state
		closedGroups.value = [...closedGroupsBeforeFilter.value];
		closedGroupsBeforeFilter.value = [];
	} else {
		// Role filter active - save current state if not already saved, and collapse all groups except Project members and the active role
		if (closedGroupsBeforeFilter.value.length === 0) {
			closedGroupsBeforeFilter.value = [...closedGroups.value];
		}
		closedGroups.value = userGroupListToDisplay.value
			.filter(
				(group) =>
					group.role?.id !== activeRoleFilterId.value &&
					group.id !== UserAssignGroups.ProjectMembers
			)
			.map((group) => group.id);
	}
};

const onRoleFilterClicked = (roleId: UserAssignSelectRole['id']) => {
	if (activeRoleFilterId.value === roleId) {
		activeRoleFilterId.value = null;
	} else {
		activeRoleFilterId.value = roleId;
		scrollToGroup(listContentElement(), headerElement());
	}

	updateGroupsForRoleFilter();
};

const onMemberAvatarClicked = () => {
	if (!isModifyEnabled.value && hasNoMembers.value) {
		return;
	}

	loadUsersAndGroups();
	loadUsersAvailability();

	if (props.taskId) {
		refetchTaskUsersAvailability({
			input: {
				taskId: props.taskId,
			},
		});
	}

	isUserSelectOpen.value = true;
	searchQuery.value = '';
	activeRoleFilterId.value = null;
	closedGroups.value = [];
	selectedRoles.value = props.assignedRoles;
	selectedUsers.value = props.userIds;
	pinnedUserPositions.value = new Map();

	nextTick(() => {
		// Reset scroll for virtual list to render from start on open
		const element = listContentElement();
		if (element) {
			element.scrollTop = 0;
		}
	});
};

const unSelectGroup = (group: string[], selected: string[]) => {
	selectedUsers.value = selected.filter(
		(userId) => !group.some((id) => id === userId)
	);
};

const updateAssignees = () => {
	const users =
		usersList.value.filter((user) =>
			selectedUsers.value.includes(user.id)
		) || [];
	const hasCupoUsers =
		usersList.value.filter((user) => user.type === UserType.Cupo).length >
		0;

	emit('update:assignees', {
		users,
		hasCupoUsers,
		shouldNotify: shouldNotify.value,
		roles: selectedRoles.value,
	});
};

const onSelectAll = (isSelectAll: boolean) => {
	if (isSelectAll) {
		selectedUsers.value = filteredUserList.value.map((user) => user.id);
	} else {
		selectedUsers.value = [];
	}
};

const toggleAllUsersSelected = (
	group: UserAssignGroup,
	isSelectAll: boolean
) => {
	const selected = [...selectedUsers.value];

	if (!isSelectAll) {
		unSelectGroup(group.userList?.map((user) => user.id) ?? [], selected);
	} else {
		group.userList.forEach((user) => selected.push(user.id));
		selectedUsers.value = uniq(selected);
	}
};

const copyAllocatedTimes = (): TaskAssigneeInputDuration => {
	const times: TaskAssigneeInputDuration = {
		users: {},
		roles: {},
	};

	if (!props.allocatedTimes) {
		return times;
	}

	props.allocatedTimes.users.forEach((time, userId) => {
		times.users[userId] = secondsToTimeAllocation(time);
	});

	props.allocatedTimes.roles.forEach((time, roleId) => {
		times.roles[roleId] = secondsToTimeAllocation(time);
	});

	return times;
};

const replaceActiveRoleWithUser = (
	userId: UserAssignSelectUserFragment['id']
) => {
	if (!activeRoleFilterId.value) {
		return;
	}

	const roleTime = props.allocatedTimes?.roles.get(activeRoleFilterId.value);

	if (roleTime) {
		const updatedTimes = copyAllocatedTimes();

		updatedTimes.users[userId] = secondsToTimeAllocation(roleTime);

		emit('update:allocated-times', updatedTimes);
	}

	const replacedRole = selectedRoles.value.find(
		(role) => role.id === activeRoleFilterId.value
	);

	if (replacedRole) {
		pinnedUserPositions.value.set(userId, replacedRole);
	}

	selectedRoles.value = selectedRoles.value.filter(
		(role) => role.id !== activeRoleFilterId.value
	);
	activeRoleFilterId.value = null;
	closedGroups.value = [...closedGroupsBeforeFilter.value];
	emit('role-replaced-with-user');
};

const onUserClicked = (id: UserAssignSelectUserFragment['id']) => {
	if (selectedUsers.value.includes(id)) {
		selectedUsers.value = selectedUsers.value.filter(
			(userId) => userId !== id
		);
		pinnedUserPositions.value.delete(id);
	} else {
		selectedUsers.value = [...selectedUsers.value, id];
		replaceActiveRoleWithUser(id);
	}
};

const onRoleClicked = (role: UserAssignSelectRole) => {
	if (selectedRoles.value.some((r) => r?.id === role?.id)) {
		selectedRoles.value = selectedRoles.value.filter(
			(r) => r?.id !== role?.id
		);
	} else {
		selectedRoles.value = [...selectedRoles.value, role];
	}
};

const onHeaderClose = () => {
	if (activeRoleFilterId.value) {
		activeRoleFilterId.value = null;
		updateGroupsForRoleFilter();
	} else {
		toggleIsUserSelectOpen();
	}
};

const onOwnerClicked = (user: UserAssignSelectUserFragment) => {
	if (entityOwner?.value?.id !== user?.id) {
		entityOwner.value = user;
	}
};

const onOwnerAvatarDblClicked = () => {
	if (!entityOwner.value || !props.enableShortcuts) {
		return;
	}

	emit('update:assignees', {
		users: [entityOwner.value],
		hasCupoUsers: false,
		shouldNotify: false,
		roles: selectedRoles.value,
	});
};

const onMemberAvatarDblClick = () => {
	if (!currentUser.value || !props.enableShortcuts) {
		return;
	}

	emit('update:assignees', {
		users: selectedUsers.value.includes(currentUser.value.id)
			? []
			: [currentUser.value],
		hasCupoUsers: false,
		shouldNotify: shouldNotify.value,
		roles: selectedRoles.value,
	});
};

const onToggleGroupDropdown = (target: HTMLElement, group: UserAssignGroup) => {
	if (target.nodeName !== 'LABEL' && target.nodeName !== 'INPUT') {
		if (closedGroups.value.includes(group.id)) {
			closedGroups.value = closedGroups.value.filter(
				(id) => id !== group.id
			);
		} else {
			closedGroups.value.push(group.id);
		}
	}
};

const onAssignUsers = () => {
	updateAssignees();
	isUserSelectOpen.value = false;
};

const onRemoveRole = () => {
	selectedRoles.value = selectedRoles.value.filter(
		(role) => role.id !== activeRoleFilterId.value
	);
	activeRoleFilterId.value = null;
	updateGroupsForRoleFilter();
};

const onAssignOwner = () => {
	emit('update:owner', entityOwner.value);
	isOwnerSelectOpen.value = false;
};

const onUpdateHasCustomAllocation = (hasCustomAllocation: boolean) => {
	if (
		hasCustomAllocation &&
		props.plannedDuration &&
		(selectedUsers.value.length > 0 || selectedRoles.value.length > 0)
	) {
		const totalCount =
			selectedUsers.value.length + selectedRoles.value.length;
		const equalTime = Math.floor(props.plannedDuration / totalCount);

		const timeInputs: TaskAssigneeInputDuration = {
			users: {},
			roles: {},
		};

		selectedUsers.value.forEach((userId) => {
			timeInputs.users[userId] = secondsToTimeAllocation(equalTime);
		});

		selectedRoles.value.forEach((role) => {
			timeInputs.roles[role.id] = secondsToTimeAllocation(equalTime);
		});

		emit('update:allocated-times', timeInputs);
	}

	emit('update:has-custom-allocation', hasCustomAllocation);
};

// these are the user groups that are defined in the settings
const userGroupList = computed<SuiSelectListGroupType[]>(() => {
	if (!result.value?.UserGroupsList) {
		return [];
	}

	return result.value.UserGroupsList.map(({ id, name, users }) => ({
		id,
		name,
		items: users
			.filter(({ status }) => status !== UserStatus.Inactive)
			.map(({ id }) => id),
	}))
		.filter((group) => group.items.length > 0)
		.sort((a, b) => a.name.localeCompare(b.name));
});

const selectedUserGroup = computed(
	(): SuiSelectListItemsAndGroupsValueType => ({
		itemIds: selectedUsers.value,
		groupIds: [],
	})
);

const onSelectUserGroup = (groupId: SuiSelectListGroupType['id']) => {
	const selectedGroup = userGroupList.value.find(
		(group) => group.id === groupId
	);

	if (selectedGroup) {
		selectedUsers.value = uniq(
			selectedUsers.value.concat(selectedGroup.items || [])
		);
	}
};

const onDeselectUserGroup = (groupId: SuiSelectListGroupType['id']) => {
	const selectedGroup = userGroupList.value.find(
		(group) => group.id === groupId
	);

	if (selectedGroup) {
		selectedUsers.value = selectedUsers.value.filter(
			(id) => !selectedGroup.items?.includes(id)
		);
	}
};

const updateUsersList = (
	result: ApolloQueryResult<UserAssignSelectUsersQuery>
) => {
	if (result.data?.UsersList) {
		usersList.value = sortUsers(result.data.UsersList);
	}

	if (result.data?.Project?.members) {
		const regularMembers = result.data.Project.members.map(
			(member) => member.id
		);
		const cupoUsers = result.data.Project.cupoUsers.map(
			(cupoUser) => cupoUser.id
		);

		projectMembers.value = [...regularMembers, ...cupoUsers];
	}
};

const avatarSize = computed<UserAssignAvatarSize>(() => {
	switch (props.size) {
		case UserAssignSelectSize.LARGE:
			return 'medium';
		case UserAssignSelectSize.SMALL:
			return 'xx-small';
		default:
			return 'x-small';
	}
});

const classAddons = computed(() => ({
	[`user-assign-select--${props.size}`]: props.size,
	[`user-assign-select--has-time-allocation`]: props.hasCustomAllocation,
}));

const measureContainerWidth = async () => {
	await nextTick();
	if ($avatarWrapper.value && props.size === UserAssignSelectSize.LARGE) {
		const rect = $avatarWrapper.value.getBoundingClientRect();
		containerWidth.value = rect.width;
	}
};

let resizeObserver: ResizeObserver | null = null;

onResultMembers((result) => {
	isMembersLoadPending.value = false;
	isInitialMembersLoading.value = false;
	updateUsersList(result);
	measureContainerWidth();
});

const resetMinHeight = async () => {
	await nextTick();

	const selectListContent = $selectList.value?.$refs?.selectListContent;

	if (selectListContent instanceof HTMLDivElement) {
		selectListContent.style.minHeight = `min(${selectListContent.offsetHeight}px, var(--sui-select-list-content-max-height))`;
	}
};

onResultAllUsers((result) => {
	hasLoadedAllUsers.value = true;
	updateUsersList(result);
	loadUsersAvailability();
	resetMinHeight();

	// Apply role filter collapse after groups are loaded. Otherwise only initial groups are collapsed.
	if (activeRoleFilterId.value) {
		updateGroupsForRoleFilter();
	}
});

const currentMembersQueryVariables = () => ({
	input: {
		filters: userAssigneeQueryFilters.value,
	},
	...baseProjectQueryParams,
});

const isMembersLoadPending = ref(false);

const loadOrRefetchMembers = () => {
	isMembersLoadPending.value = true;
	const variables = currentMembersQueryVariables();
	if (!loadMembers(UserAssignSelectUsersDocument, variables)) {
		refetchMembers(variables);
	}
};

onMounted(() => {
	if (props.preloadedUsers.length > 0) {
		usersList.value = sortUsers(props.preloadedUsers);

		const preloadedIds = new Set(props.preloadedUsers.map((u) => u.id));
		const hasMissingUsers = props.userIds.some(
			(id) => !preloadedIds.has(id)
		);

		if (hasMissingUsers) {
			loadOrRefetchMembers();
		} else {
			isInitialMembersLoading.value = false;
		}
	} else if (props.userIds.length > 0) {
		loadOrRefetchMembers();
	} else {
		isInitialMembersLoading.value = false;
	}

	if (
		props.size === UserAssignSelectSize.LARGE &&
		typeof ResizeObserver !== 'undefined'
	) {
		resizeObserver = new ResizeObserver(() => {
			measureContainerWidth();
		});
	}

	measureContainerWidth();
});

const cleanupResizeObserver = () => {
	if (resizeObserver) {
		resizeObserver.disconnect();
		resizeObserver = null;
	}
};

watch(
	() => props.preloadedUsers,
	(newUsers) => {
		if (
			!isUserSelectOpen.value &&
			usersList.value.length <= newUsers.length
		) {
			usersList.value = sortUsers(newUsers);
			projectMembers.value = props.userIds;
		}
	}
);

watch(
	() => props.userIds,
	() => {
		if (
			!isMembersLoadPending.value &&
			(result.value?.UserGroupsList.length ?? 0) === 0 &&
			props.userIds.length > 0
		) {
			const allUsersCovered = props.userIds.every((id) =>
				usersList.value.some((u) => u.id === id)
			);

			if (!allUsersCovered) {
				loadOrRefetchMembers();
			}
		}
		measureContainerWidth();
	}
);

watch(
	() => props.size,
	(newSize, oldSize) => {
		if (
			newSize === UserAssignSelectSize.LARGE &&
			oldSize !== UserAssignSelectSize.LARGE
		) {
			if (typeof ResizeObserver !== 'undefined' && !resizeObserver) {
				resizeObserver = new ResizeObserver(() => {
					measureContainerWidth();
				});

				nextTick(() => {
					if ($avatarWrapper.value && resizeObserver) {
						resizeObserver.observe($avatarWrapper.value);
					}
				});
			}

			measureContainerWidth();
		} else if (newSize !== UserAssignSelectSize.LARGE && resizeObserver) {
			cleanupResizeObserver();
			containerWidth.value = 0;
		}
	},
	{ immediate: true }
);

watch($avatarWrapper, (newRef) => {
	if (newRef && props.size === UserAssignSelectSize.LARGE && resizeObserver) {
		resizeObserver.observe(newRef);
		measureContainerWidth();
	}
});

watch(
	[selectedUsers, selectedRoles],
	(
		[newSelectedUsers, newSelectedRoles],
		[oldSelectedUsers, oldSelectedRoles]
	) => {
		if (props.allocatedTimes && oldSelectedUsers && oldSelectedRoles) {
			const addedUsers = newSelectedUsers.filter(
				(userId) => !oldSelectedUsers.includes(userId)
			);
			const addedRoles = newSelectedRoles.filter(
				(role) =>
					!oldSelectedRoles.some((oldRole) => oldRole.id === role.id)
			);

			if (addedUsers.length > 0 || addedRoles.length > 0) {
				const currentTimes = copyAllocatedTimes();

				addedUsers.forEach((userId) => {
					// Time can be present if user is replacing a role
					if (!currentTimes.users[userId]) {
						currentTimes.users[userId] = '0';
					}
				});

				addedRoles.forEach((role) => {
					if (!currentTimes.roles[role.id]) {
						currentTimes.roles[role.id] = '0';
					}
				});

				emit('update:allocated-times', currentTimes);
			}
		}
	},
	{ deep: true }
);

watch(searchQuery, (newQuery, oldQuery) => {
	if (!activeRoleFilterId.value) {
		return;
	}

	// Query change, expand groups with results
	if (newQuery !== '') {
		const groupsWithMatches: UserAssignGroup['id'][] = [];

		userGroupListToDisplay.value.forEach((group) => {
			if (group.userList.length > 0) {
				groupsWithMatches.push(group.id);
			}
		});

		closedGroups.value = userGroupListToDisplay.value
			.filter((group) => !groupsWithMatches.includes(group.id))
			.map((group) => group.id);
	}

	// Query cleared, filter still active - update groups and scroll to group
	if (oldQuery !== '' && newQuery === '') {
		updateGroupsForRoleFilter();
		scrollToGroup(listContentElement(), headerElement());
	}
});
</script>

<style lang="scss">
.user-assign-select {
	display: flex;
	align-items: flex-start;
	font-size: 0;
	padding-left: 1rem;

	&__dropdown--disabled {
		pointer-events: none;
	}

	&__dropdown-wrapper {
		width: 100%;
	}

	&__dropdown {
		display: inline-block;
	}

	&__arrow-icon {
		margin: 0 0.5rem;
		flex-shrink: 0;
	}

	&__avatar-wrapper {
		display: flex;
	}

	&__avatar {
		position: relative;
		z-index: 2;

		&:not(:first-child) {
			margin-left: -1rem;
		}

		&-name {
			position: absolute;
			text-align: center;
			margin-top: 0.5rem;
			font-size: 1.375rem;
			line-height: 2rem;
			white-space: nowrap;
			width: 100%;
			max-width: 100%;
			text-overflow: ellipsis;
			overflow: hidden;
		}

		&-time {
			color: $neutral-60;
			width: 100%;
			font-size: 1.375rem;
			line-height: 2rem;
			position: absolute;
			margin-top: 2.5rem;
			text-align: center;
			white-space: nowrap;
		}
	}

	&__round-icon-wrapper {
		--sui-icon-fill: #{$neutral-70};

		width: 3.5rem;
		height: 3.5rem;
		border-radius: 50%;
		display: flex;
		justify-content: center;
		align-items: center;
		color: $neutral-00;
		border: 1px dashed $neutral-60;

		&:hover {
			--sui-icon-fill: #{$neutral-80};

			transition: all 0.3s ease;
			border-color: $neutral-80;
			background-color: rgba($neutral-50, 0.15);
		}
	}

	&__group-wrapper {
		display: flex;
		justify-content: flex-start;

		&:hover {
			cursor: pointer;
		}
	}

	&__group-name {
		font-size: 1.625rem;
		font-weight: $scoro-fw-semi-bold;
	}

	&__expand-icon {
		margin-left: auto;
		transition: transform 0.2s ease;

		&--rotate {
			transform: rotate(-90deg);
		}
	}

	&__tick {
		--sui-icon-fill: #{$blue-50};

		margin-left: auto;
	}

	&__footer {
		&--with-notify {
			display: grid;
			grid-template-columns: 1fr 1fr 1fr;
			width: 100%;
			place-items: center center;
		}
	}

	&__notify-checkbox {
		justify-self: flex-start;
	}

	&--small {
		.user-assign-select__round-icon {
			transform: scale(0.8);
		}

		.user-assign-select__round-icon-wrapper {
			width: 2.5rem;
			height: 2.5rem;
		}

		.user-assign-select__arrow-icon {
			margin: 0.25rem 1rem 0 0;
		}

		.user-assign-select__avatar-wrapper {
			gap: 0.25rem;
		}
	}

	&--medium {
		align-items: center;
	}

	&--large {
		width: 100%;
		margin-bottom: 2rem;
		padding-left: 0;

		.user-assign-select__round-icon-wrapper {
			width: 5rem;
			height: 5rem;
			margin-left: 0;
		}

		.user-assign-select__arrow-icon {
			margin: 1.5rem 1rem 0;
		}

		.user-assign-select__avatar-wrapper {
			gap: 1rem;
		}

		.user-assign-select__avatar {
			margin-left: 0;
			padding-bottom: 2rem;
		}

		&.user-assign-select--has-time-allocation {
			.user-assign-select__avatar {
				padding-bottom: 4rem;
			}
		}
	}
}

.sui-select-list-helpers {
	&.user-assign-select__group-wrapper {
		padding: 1.5rem 2rem;
	}

	&.user-assign-select__select-all-wrapper {
		min-height: 5rem;
		padding: 0.5rem 2rem;
	}
}
</style>
